import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';

export interface B2BTrafficFineCaseDto {
  id: number;
  trafficFineId: number;
  b2BContractId: number;
  contractNumber?: string | null;
  status: string;
  driverIndicationId?: number | null;
  indicatedByActor?: string | null;
  indicatedByName?: string | null;
  indicatedAt?: string | null;
  openedAt: string;
  closedAt?: string | null;
  lastAlertAt?: string | null;
  notes?: string | null;
  aipNumber?: string | null;
  infractionStatus?: string | null;
  paymentStatus?: string | null;
  originalAmount?: number | null;
  infractionDateTime?: string | null;
  dueDate?: string | null;
  vehicleId?: number | null;
  vehiclePlate?: string | null;
  histories?: B2BTrafficFineCaseHistoryDto[] | null;
}

export interface B2BTrafficFineCaseHistoryDto {
  id: number;
  fromStatus?: string | null;
  toStatus?: string | null;
  eventType: string;
  message?: string | null;
  actorName?: string | null;
  actorScope?: string | null;
  occurredAt: string;
}

export interface B2BTrafficFineCaseCommentDto {
  id: number;
  mensagem: string;
  autor: string;
  autorOrigem: string;
  interno: boolean;
  criadoEm: string;
}

export interface B2BTrafficFineCaseAlertDto {
  id: number;
  trafficFineId: number;
  b2BContractId: number;
  status: string;
  lastAlertAt?: string | null;
  indicatedByName?: string | null;
}

@Injectable({ providedIn: 'root' })
export class B2BTrafficFineCasesService {
  constructor(private http: HttpClient) {}

  private url(pathSuffix = ''): string {
    return buildFinancialApiUrl('b2b-traffic-fine-cases', pathSuffix);
  }

  private pick<T = any>(obj: any, ...keys: string[]): T | undefined {
    if (!obj) return undefined;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
    }
    return undefined;
  }

  /** Normaliza PascalCase/camelCase da API Financial. */
  normalizeCase(raw: any): B2BTrafficFineCaseDto | null {
    if (!raw || typeof raw !== 'object') return null;
    const id = Number(this.pick(raw, 'id', 'Id') ?? 0);
    if (!id) return null;
    return {
      id,
      trafficFineId: Number(this.pick(raw, 'trafficFineId', 'TrafficFineId') ?? 0),
      b2BContractId: Number(
        this.pick(raw, 'b2BContractId', 'B2BContractId', 'b2bContractId') ?? 0
      ),
      contractNumber: this.pick(raw, 'contractNumber', 'ContractNumber') ?? null,
      status: String(this.pick(raw, 'status', 'Status') ?? ''),
      driverIndicationId: this.pick(raw, 'driverIndicationId', 'DriverIndicationId') ?? null,
      indicatedByActor: this.pick(raw, 'indicatedByActor', 'IndicatedByActor') ?? null,
      indicatedByName: this.pick(raw, 'indicatedByName', 'IndicatedByName') ?? null,
      indicatedAt: this.pick(raw, 'indicatedAt', 'IndicatedAt') ?? null,
      openedAt: String(this.pick(raw, 'openedAt', 'OpenedAt') ?? ''),
      closedAt: this.pick(raw, 'closedAt', 'ClosedAt') ?? null,
      lastAlertAt: this.pick(raw, 'lastAlertAt', 'LastAlertAt') ?? null,
      notes: this.pick(raw, 'notes', 'Notes') ?? null,
      aipNumber: this.pick(raw, 'aipNumber', 'AipNumber', 'AIPNumber') ?? null,
      infractionStatus: this.pick(raw, 'infractionStatus', 'InfractionStatus') ?? null,
      paymentStatus: this.pick(raw, 'paymentStatus', 'PaymentStatus') ?? null,
      originalAmount: this.pick(raw, 'originalAmount', 'OriginalAmount') ?? null,
      infractionDateTime: this.pick(raw, 'infractionDateTime', 'InfractionDateTime') ?? null,
      dueDate: this.pick(raw, 'dueDate', 'DueDate') ?? null,
      vehicleId: this.pick(raw, 'vehicleId', 'VehicleId') ?? null,
      vehiclePlate: this.pick(raw, 'vehiclePlate', 'VehiclePlate') ?? null,
      histories: this.pick(raw, 'histories', 'Histories') ?? null
    };
  }

  private normalizeComment(raw: any): B2BTrafficFineCaseCommentDto {
    return {
      id: Number(this.pick(raw, 'id', 'Id') ?? 0),
      mensagem: String(this.pick(raw, 'mensagem', 'Mensagem', 'message', 'Message') ?? ''),
      autor: String(this.pick(raw, 'autor', 'Autor', 'authorName', 'AuthorName') ?? ''),
      autorOrigem: String(this.pick(raw, 'autorOrigem', 'AutorOrigem', 'authorScope', 'AuthorScope') ?? ''),
      interno: !!(this.pick(raw, 'interno', 'Interno', 'internal', 'Internal')),
      criadoEm: String(this.pick(raw, 'criadoEm', 'CriadoEm', 'createdAt', 'CreatedAt') ?? '')
    };
  }

  assign(trafficFineId: number, b2bContractId: number, actorName?: string, notes?: string): Observable<B2BTrafficFineCaseDto> {
    return this.http.post<any>(this.url('/assign'), {
      trafficFineId,
      TrafficFineId: trafficFineId,
      b2BContractId: b2bContractId,
      B2BContractId: b2bContractId,
      actorName,
      ActorName: actorName,
      notes
    }).pipe(
      map(raw => {
        const c = this.normalizeCase(raw);
        if (!c) throw new Error('Resposta de atribuição inválida.');
        return c;
      })
    );
  }

  getById(caseId: number, contractId?: number | null): Observable<B2BTrafficFineCaseDto | null> {
    let params = new HttpParams();
    if (contractId) params = params.set('contractId', String(contractId));
    return this.http.get<any>(this.url(`/${caseId}`), { params }).pipe(
      map(raw => this.normalizeCase(raw)),
      catchError(() => of(null))
    );
  }

  getByFineId(trafficFineId: number): Observable<B2BTrafficFineCaseDto | null> {
    return this.http.get<any>(this.url(`/by-fine/${trafficFineId}`)).pipe(
      map(raw => this.normalizeCase(raw)),
      catchError(() => of(null))
    );
  }

  list(opts: { contractId?: number | null; status?: string | null; page?: number; pageSize?: number } = {}): Observable<{
    items: B2BTrafficFineCaseDto[];
    totalItems: number;
  }> {
    let params = new HttpParams()
      .set('page', String(opts.page ?? 1))
      .set('pageSize', String(opts.pageSize ?? 50));
    if (opts.contractId) params = params.set('contractId', String(opts.contractId));
    if (opts.status) params = params.set('status', opts.status);

    return this.http.get<any>(this.url(), { params }).pipe(
      map(res => {
        const itemsRaw = (res?.items ?? res?.Items ?? []) as any[];
        return {
          items: itemsRaw.map(x => this.normalizeCase(x)).filter((x): x is B2BTrafficFineCaseDto => !!x),
          totalItems: res?.totalItems ?? res?.TotalItems ?? 0
        };
      }),
      catchError(() => of({ items: [], totalItems: 0 }))
    );
  }

  transitionStatus(caseId: number, toStatus: string, actorName?: string, message?: string, contractId?: number | null): Observable<B2BTrafficFineCaseDto> {
    return this.http.post<any>(this.url(`/${caseId}/status`), {
      toStatus,
      ToStatus: toStatus,
      actorName,
      ActorName: actorName,
      message,
      b2BContractId: contractId ?? undefined,
      B2BContractId: contractId ?? undefined
    }).pipe(
      map(raw => {
        const c = this.normalizeCase(raw);
        if (!c) throw new Error('Resposta de status inválida.');
        return c;
      })
    );
  }

  getComments(caseId: number, contractId?: number | null): Observable<B2BTrafficFineCaseCommentDto[]> {
    let params = new HttpParams();
    if (contractId) params = params.set('contractId', String(contractId));
    return this.http.get<any[]>(this.url(`/${caseId}/comentarios`), { params }).pipe(
      map(list => (Array.isArray(list) ? list : []).map(x => this.normalizeComment(x))),
      catchError(() => of([]))
    );
  }

  postComment(caseId: number, mensagem: string, opts: { interno?: boolean; actorName?: string; staffId?: number | null; contractId?: number | null } = {}): Observable<B2BTrafficFineCaseCommentDto> {
    return this.http.post<any>(this.url(`/${caseId}/comentarios`), {
      mensagem,
      interno: !!opts.interno,
      actorName: opts.actorName,
      staffId: opts.staffId,
      b2BContractId: opts.contractId ?? undefined,
      B2BContractId: opts.contractId ?? undefined
    }).pipe(map(raw => this.normalizeComment(raw)));
  }

  getAlerts(contractId?: number | null, after?: string | null): Observable<B2BTrafficFineCaseAlertDto[]> {
    let params = new HttpParams();
    if (contractId) params = params.set('contractId', String(contractId));
    if (after) params = params.set('after', after);
    return this.http.get<any[]>(this.url('/alerts'), { params }).pipe(
      map(list =>
        (Array.isArray(list) ? list : []).map(x => ({
          id: Number(this.pick(x, 'id', 'Id') ?? 0),
          trafficFineId: Number(this.pick(x, 'trafficFineId', 'TrafficFineId') ?? 0),
          b2BContractId: Number(this.pick(x, 'b2BContractId', 'B2BContractId', 'b2bContractId') ?? 0),
          status: String(this.pick(x, 'status', 'Status') ?? ''),
          lastAlertAt: this.pick(x, 'lastAlertAt', 'LastAlertAt') ?? null,
          indicatedByName: this.pick(x, 'indicatedByName', 'IndicatedByName') ?? null
        }))
      ),
      catchError(() => of([]))
    );
  }

  getEvidences(caseId: number, contractId?: number | null): Observable<B2BTrafficFineCaseEvidenceDto[]> {
    let params = new HttpParams();
    if (contractId) params = params.set('contractId', String(contractId));
    return this.http.get<any[]>(this.url(`/${caseId}/evidencias`), { params }).pipe(
      map(list =>
        (Array.isArray(list) ? list : []).map(x => ({
          id: Number(this.pick(x, 'id', 'Id') ?? 0),
          fileName: String(this.pick(x, 'fileName', 'FileName') ?? ''),
          filePath: String(this.pick(x, 'filePath', 'FilePath') ?? ''),
          fileType: this.pick(x, 'fileType', 'FileType') ?? null,
          mimeType: this.pick(x, 'mimeType', 'MimeType') ?? null,
          fileSize: this.pick(x, 'fileSize', 'FileSize') ?? null,
          description: this.pick(x, 'description', 'Description') ?? null,
          uploadedBy: this.pick(x, 'uploadedBy', 'UploadedBy') ?? null,
          uploadDate: this.pick(x, 'uploadDate', 'UploadDate') ?? null
        }))
      ),
      catchError(() => of([]))
    );
  }

  /** Upload binário: body = File; query fileName. */
  uploadEvidence(caseId: number, file: File, actorName?: string): Observable<B2BTrafficFineCaseEvidenceDto> {
    const q = `?fileName=${encodeURIComponent(file.name)}`;
    return this.http
      .post<any>(this.url(`/${caseId}/evidencias/upload${q}`), file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          ...(actorName ? { 'X-Actor-Name': actorName } : {})
        }
      })
      .pipe(
        map(x => ({
          id: Number(this.pick(x, 'id', 'Id') ?? 0),
          fileName: String(this.pick(x, 'fileName', 'FileName') ?? file.name),
          filePath: String(this.pick(x, 'filePath', 'FilePath') ?? ''),
          fileType: this.pick(x, 'fileType', 'FileType') ?? null,
          mimeType: this.pick(x, 'mimeType', 'MimeType') ?? file.type ?? null,
          fileSize: this.pick(x, 'fileSize', 'FileSize') ?? file.size,
          description: null,
          uploadedBy: actorName ?? null,
          uploadDate: this.pick(x, 'uploadDate', 'UploadDate') ?? null
        }))
      );
  }
}

export interface B2BTrafficFineCaseEvidenceDto {
  id: number;
  fileName: string;
  filePath: string;
  fileType?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  description?: string | null;
  uploadedBy?: string | null;
  uploadDate?: string | null;
}
